import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  useTheme,
  Divider,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Collapse
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { useLicense } from '../contexts/LicenseContext';
import licenseService from '../services/licenseService';
import { toServerDate } from '../timezone';

const LicenseBlockedPage = () => {
  const theme = useTheme();
  const { licenseInfo, refreshLicense, loading } = useLicense();
  const [machineId, setMachineId] = useState(null);
  const [loadingMachineId, setLoadingMachineId] = useState(false);
  const [showMachineInfo, setShowMachineInfo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [machineIdError, setMachineIdError] = useState(null);

  // Fetch machine ID when section is expanded (available to all users)
  useEffect(() => {
    if (showMachineInfo && !machineId && !loadingMachineId) {
      fetchMachineId();
    }
  }, [showMachineInfo]);

  const fetchMachineId = async () => {
    try {
      setLoadingMachineId(true);
      setMachineIdError(null);
      const response = await licenseService.getMachineId();
      setMachineId(response.data?.data || response.data);
    } catch (err) {
      console.error('Failed to fetch machine ID:', err);
      setMachineIdError(err.response?.data?.message || 'Failed to retrieve machine information');
    } finally {
      setLoadingMachineId(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshLicense();
    } catch (err) {
      // Error is handled in context
    }
  };

  const handleCopyMachineId = () => {
    if (machineId?.machineId) {
      navigator.clipboard.writeText(machineId.machineId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusInfo = () => {
    switch (licenseInfo?.status) {
      case 'EXPIRED':
        return {
          title: 'License Expired',
          message: 'Your license has expired and the grace period has ended. Access to the platform is temporarily suspended.',
          color: theme.palette.error.main
        };
      case 'INVALID_SIGNATURE':
        return {
          title: 'Invalid License',
          message: 'The license file appears to be corrupted or tampered with. Please contact support for a new license.',
          color: theme.palette.error.main
        };
      case 'INVALID_MACHINE':
        return {
          title: 'Machine ID Mismatch',
          message: 'This license is not valid for this machine. The hardware configuration does not match the licensed machine.',
          color: theme.palette.error.main
        };
      case 'REVOKED':
        return {
          title: 'License Revoked',
          message: 'This license has been revoked. Please contact support for assistance.',
          color: theme.palette.error.main
        };
      case 'NOT_FOUND':
        return {
          title: 'License Not Found',
          message: 'No valid license file was found. Please obtain a license file and place it in the application directory.',
          color: theme.palette.warning.main
        };
      case 'CLOCK_TAMPERED':
        return {
          title: 'Clock Tampering Detected',
          message: 'The system clock appears to have been set backwards. Please restore the correct system time and restart the application.',
          color: theme.palette.error.main
        };
      default:
        return {
          title: 'License Issue',
          message: 'There is an issue with your license. Please contact support for assistance.',
          color: theme.palette.error.main
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        p: 3
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            borderTop: `4px solid ${statusInfo.color}`
          }}
        >
          <ErrorIcon
            sx={{
              fontSize: 80,
              color: statusInfo.color,
              mb: 2
            }}
          />

          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            {statusInfo.title}
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph>
            {statusInfo.message}
          </Typography>

          {licenseInfo?.expirationDate && (
            <Alert severity="info" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
              <AlertTitle>License Details</AlertTitle>
              <Typography variant="body2">
                <strong>Expiration Date:</strong>{' '}
                {toServerDate(licenseInfo.expirationDate)?.toLocaleDateString()}
              </Typography>
              {licenseInfo?.graceExpirationDate && (
                <Typography variant="body2">
                  <strong>Grace Period End:</strong>{' '}
                  {toServerDate(licenseInfo.graceExpirationDate)?.toLocaleDateString()}
                </Typography>
              )}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Checking...' : 'Check License Again'}
            </Button>

            <Typography variant="body2" color="text.secondary">
              If you have renewed your license, click the button above to refresh.
            </Typography>
          </Box>

          {/* Machine ID Section - Available to all users */}
          <Divider sx={{ my: 3 }} />

          <Button
            onClick={() => setShowMachineInfo(!showMachineInfo)}
            endIcon={showMachineInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            startIcon={<ComputerIcon />}
            fullWidth
            variant="outlined"
            color="primary"
          >
            {showMachineInfo ? 'Hide Machine ID' : 'Get Machine ID for License'}
          </Button>

          <Collapse in={showMachineInfo}>
            <Box sx={{ mt: 2, textAlign: 'left' }}>
              {loadingMachineId ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : machineIdError ? (
                <Alert severity="error">
                  {machineIdError}
                </Alert>
              ) : machineId ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Machine ID (copy this to generate a license)
                  </Typography>
                  <TextField
                    fullWidth
                    value={machineId.machineId || ''}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                            <IconButton onClick={handleCopyMachineId} edge="end">
                              <CopyIcon color={copied ? 'success' : 'inherit'} />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mt: 1, mb: 2 }}
                  />

                  <Alert severity="info" sx={{ mt: 1 }}>
                    <AlertTitle>How to get a license</AlertTitle>
                    <Typography variant="body2">
                      1. Copy the Machine ID above<br />
                      2. Go to the Licensing Server portal<br />
                      3. Create a new license using this Machine ID<br />
                      4. Download the license.lic file<br />
                      5. Place it in the application directory<br />
                      6. Click "Check License Again"
                    </Typography>
                  </Alert>

                  <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>CPU Cores:</strong> {machineId.cpuCores}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>OS:</strong> {machineId.osName}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Alert severity="error">
                  Unable to retrieve machine information. Please check server logs.
                </Alert>
              )}
            </Box>
          </Collapse>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" gutterBottom>
              Need Help?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  support@verops.io
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LicenseBlockedPage;
